import { errorWrapper } from 'utility/error';

import type { DateResolution } from './__GENERATED';
import { apiClient } from './api';
import { toEmployeeHourDomain } from './transformers/toEmployeeHourDomain';

// Maps a `Time.Date` onto the generated `DateResolution` shape. Every field
// is nullable on the wire, so unused components (e.g. `dayOfMonth` on a
// month-level pick) come through as null rather than undefined. The legacy
// `apiUtility.toDateResolution` produces a slightly different ambient type
// that's not assignable here, hence this local mapper.
function toDateResolution(date: Time.Date): DateResolution {
  const base: DateResolution = {
    year: date.year,
    monthNumber: null,
    weekNumber: null,
    dayOfMonth: null,
    dayOfWeek: null,
  };

  switch (date.type) {
    case 'year':
      return base;
    case 'month':
      return { ...base, monthNumber: date.month + 1 };
    case 'week':
      return { ...base, weekNumber: date.week };
    case 'day':
    case 'hour':
      return {
        ...base,
        monthNumber: date.month + 1,
        dayOfMonth: date.dayOfMonth,
      };
  }
}

// Fetches aggregated activity rows at `Employee` resolution for a specific
// set of dates and maps them onto the `Data.AggregatedEmployeeHour` domain.
//
// The endpoint is the same one `getAggregatedActivities` uses — Employee
// resolution fans rows out per (employee × activity × date). `dates` is
// required: an empty list would tell the server "no date filter" and load
// the full history at employee resolution, which would be punishing.
async function getEmployeeHours(args: {
  tenantId: string;
  dates: Time.Date[];
}) {
  const { tenantId, dates } = args;

  const apiCall = apiClient.ProducedActivity_GetAggregatedActivities(
    {
      resolution: 'Employee',
      dates: dates.map(toDateResolution),
    },
    {
      params: { tenantId },
    },
  );

  return await errorWrapper({
    apiCall,
    error: {
      message: '',
      severity: 'warning',
      title: 'Failed to load employee hours',
    },
    onSuccess: (dtos) => {
      const { employeeHourDomain } = toEmployeeHourDomain({
        dtos: dtos ?? [],
      });
      return { employeeHourDomain };
    },
  });
}

export { getEmployeeHours };
